// @flow
import { useRef, useEffect, useCallback, useState } from 'react';
import _isEqual from 'lodash/isEqual';

import { handleError } from '../utils/errors';

const loadingIcon = '/images/loading-diamond.gif';
const errorIcon = '/images/image-failed-to-load.gif';
const secondsBeforeShowingLoadingIcon = 2;
const secondsBeforeReportingWarning = 5;

type TImage = {
  src: string,
  alt?: string,
  styles?: { [string]: string | number },
  className?: string
};

type TProps = {
  images: Array<TImage>,
  imageRef?: { current: HTMLImageElement | null | void },
  styles?: { [string]: string | number },
  loadingClassName?: string
};

function reportWarning(imageUrl: string) {
  handleError(
    new Error(
      `${imageUrl} took at least ${secondsBeforeReportingWarning} seconds to load`
    )
  );
}

function ProgressiveImage({
  images: imagesProp,
  imageRef: ref,
  styles,
  loadingClassName
}: TProps) {
  const imageRef = useRef(new Image());
  const [images, setImages] = useState(imagesProp);
  const loadingIndexRef = useRef(0);
  const loadingIntervalRef = useRef(null);
  const loadingIntervalIterationRef = useRef(0);
  const [lastLoadedImage, setLastLoadedImage] = useState(null);
  const [displayErrorImage, setDisplayErrorImage] = useState(false);
  const [displayLoadingIcon, setDisplayLoadingIcon] = useState(false);
  const [currentLoadUrl, setCurrentLoadUrl] = useState();

  let imageStyles;

  if (lastLoadedImage) {
    imageStyles = { ...lastLoadedImage.styles, ...styles };
  } else {
    imageStyles = styles;
  }

  const onLoad = useCallback(() => {
    clearInterval(loadingIntervalRef.current);
    setLastLoadedImage(images[loadingIndexRef.current]);
    setDisplayLoadingIcon(false);
    setDisplayErrorImage(false);

    if (loadingIndexRef.current < images.length - 1) {
      loadingIndexRef.current += 1;
      imageRef.current.src = images[loadingIndexRef.current].src;
      setCurrentLoadUrl(imageRef.current.src);
    }
  }, [images]);

  const onError = useCallback(() => {
    clearInterval(loadingIntervalRef.current);

    // do we have at least one more image in the array?
    if (loadingIndexRef.current < images.length - 1) {
      loadingIndexRef.current += 1;
      imageRef.current.src = images[loadingIndexRef.current].src;
      setCurrentLoadUrl(imageRef.current.src);
    } else if (!lastLoadedImage) {
      setDisplayErrorImage(true);
    }
  }, [images, lastLoadedImage]);

  useEffect(() => {
    const image = imageRef.current;

    image.onload = onLoad;
    image.onerror = onError;

    return () => {
      image.onload = undefined;
      image.onerror = undefined;
    };
  }, [onLoad, onError]);

  useEffect(() => {
    const nextImage = images[loadingIndexRef.current];

    if (nextImage) {
      imageRef.current.src = nextImage.src;
      setCurrentLoadUrl(nextImage.src);
    }
  }, [images]);

  useEffect(() => {
    if (!currentLoadUrl) return;

    // ensure that we can't accidentally orphan intervals
    clearInterval(loadingIntervalRef.current);

    loadingIntervalIterationRef.current = 0;
    loadingIntervalRef.current = setInterval(() => {
      loadingIntervalIterationRef.current += 1;

      if (
        loadingIntervalIterationRef.current === secondsBeforeShowingLoadingIcon
      ) {
        setDisplayLoadingIcon(true);
      } else if (
        loadingIntervalIterationRef.current === secondsBeforeReportingWarning
      ) {
        reportWarning(currentLoadUrl);
      }
    }, 1000);
  }, [currentLoadUrl]);

  if (!_isEqual(imagesProp, images)) {
    loadingIndexRef.current = 0;
    setImages(imagesProp);
    setLastLoadedImage(null);

    clearInterval(loadingIntervalRef.current);
  }

  if (lastLoadedImage) {
    return (
      <img
        src={lastLoadedImage.src}
        className={lastLoadedImage.className}
        style={imageStyles}
        alt={lastLoadedImage.alt || ''}
        ref={ref}
      />
    );
  } else if (displayErrorImage) {
    return (
      <img
        src={errorIcon}
        style={imageStyles}
        alt='failed to load, please try again'
        ref={ref}
      />
    );
  } else if (displayLoadingIcon) {
    return (
      <img
        src={loadingIcon}
        style={imageStyles}
        alt='Loading...'
        className={loadingClassName}
        ref={ref}
      />
    );
  } else {
    return null;
  }
}

export default ProgressiveImage;
